import { FilterFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { AbaTicketType, AbaViewItemType } from "@src/types/ab-testing-exploration";
import dayjs from "dayjs";
import { uniq } from "lodash";
import minMax from "dayjs/plugin/minMax";
import { config } from "@src/config";
dayjs.extend(minMax);

const PLATFORM_MAP: Record<string, string> = {
  ios: "i",
  android: "a",
};

const DIMENSION_KEYS = [
  "country_code",
  "media_source",
  "device_category",
  "device_model",
  "first_app_version",
  "last_app_version",
  "platform",
];

const EXTRA_FILTER_MAPPINGS = [
  { key: "country_code", formField: FilterFormFieldsEnum.EXTRA_COUNTRY },
  { key: "media_source", formField: FilterFormFieldsEnum.EXTRA_MEDIA_SOURCE },
  { key: "device_category", formField: FilterFormFieldsEnum.EXTRA_DEVICE_CATEGORY },
  { key: "device_model", formField: FilterFormFieldsEnum.EXTRA_DEVICE_MODAL },
  { key: "first_app_version", formField: FilterFormFieldsEnum.EXTRA_FIRST_APP_VERSION },
  { key: "last_app_version", formField: FilterFormFieldsEnum.EXTRA_LAST_APP_VERSION },
];
const getRangeDateSelected = (dateSelected: string[]) => {
  const convertDateInstances = dateSelected.map((date) => dayjs(date));
  const minDate = dayjs.min(convertDateInstances);
  const maxDate = dayjs.max(convertDateInstances);
  return [dayjs(minDate).format(config.DATE_FORMAT), dayjs(maxDate).format(config.DATE_FORMAT)];
};

export const processingAbaConfigToForm = (ticket: AbaTicketType, viewConfig: AbaViewItemType): Record<string, any> => {
  const parsedFilter = JSON.parse(viewConfig.filter || "{}");
  const cohortType = parsedFilter?.cohort_type;
  const dateTime = parsedFilter[cohortType] ?? [];

  const mappingData: Record<string, any> = {
    product_code: viewConfig?.productCode,
    aba_id: viewConfig.experimentId,
    variants: uniq([...viewConfig.compareVariants, viewConfig.baseVariant]),
    base_variant: viewConfig.baseVariant,
    day_diff: viewConfig.dayDiff,
    date_time: getRangeDateSelected(dateTime),
    cohort_type: cohortType,
  };

  DIMENSION_KEYS.forEach((key) => {
    const values = parsedFilter[key];
    if (values) mappingData[key] = values;
  });

  return generateToFormStructure(mappingData);
};

export const generateToFormStructure = (configData: Record<string, any>): Record<string, any> => {
  const platformCodes = configData?.platform?.map((p: string) => PLATFORM_MAP[p]) ?? undefined;

  const form: Record<string, any> = {
    [FilterFormFieldsEnum.PRODUCT_CODE]: configData.product_code,
    [FilterFormFieldsEnum.COHORT_DATE]:
      configData.cohort_type === "onboarding_date" ? "first_signal_date" : "install_date_tzutc",
    [FilterFormFieldsEnum.MAX_DAY_DIFF]: configData.day_diff ?? 0,
    [FilterFormFieldsEnum.INVALID_COUNTRIES]: true,
    [FilterFormFieldsEnum.USERS_JOIN_MULTIPLE_GROUPS]: true,
    [FilterFormFieldsEnum.MULTI_USER_PSEUDO_ID]: true,
    [FilterFormFieldsEnum.LATE_ONBOARDING_DATE]: true,
    [FilterFormFieldsEnum.ABA_ID]: Number(configData.aba_id) ?? null,
    ...(platformCodes && {
      [FilterFormFieldsEnum.PLATFORM]: platformCodes,
      [FilterFormFieldsEnum.AMA_APP_IDS]: platformCodes.map((p: string) => `${configData.product_code}${p}`),
    }),
    [FilterFormFieldsEnum.VARIANTS]: configData.variants ?? [],
    [FilterFormFieldsEnum.BASE_VARIANTS]: configData.base_variant ?? "",
  };

  const dateField =
    configData.cohort_type === "onboarding_date"
      ? FilterFormFieldsEnum.EXTRA_ONBOARDING_DATE
      : FilterFormFieldsEnum.EXTRA_INSTALL_DATE;

  form[dateField] = configData.date_time ?? [null, null];

  EXTRA_FILTER_MAPPINGS.forEach(({ key, formField }) => {
    if (configData[key]) {
      form[formField] = configData[key] ?? [];
    }
  });

  return form;
};
